import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { cloneDeep } from 'Utils/lodashUtils';
import moment from 'moment';
import { Calendar } from 'react-date-range';

import TextField from 'Components/forms/text-field/TextField';

import {
  getAvailableRanges,
  isValidDate,
} from 'Utils/dateUtils';
import './DualCalendar.scss';

class DualCalendar extends Component {
  constructor(props, context) {
    super(props, context);
    const {
      startDate,
      endDate,
      dateFormat,
      translations,
    } = props;

    this.availableRanges = getAvailableRanges(translations);

    const presetRangeObject = this.availableRanges.find((range) => range.value === startDate);
    const preset = (presetRangeObject)
      ? presetRangeObject.value
      : this.availableRanges.find((range) => range.value === 'custom').value;

    const { parsedStart, parsedEnd } = this.getRelativeDateValues(preset, startDate, endDate, dateFormat);

    this.state = {
      dates: {
        startDate: parsedStart,
        endDate: parsedEnd,
      },
      tempDates: {
        startDate: parsedStart,
        endDate: parsedEnd,
      },
      errors: {
        start: null,
        end: null,
      },
      preset,
    };

    this.attachBindings();
  }

  componentDidMount() {
    this.props.onRef(this); // eslint-disable-line react/prop-types
  }

  componentWillUnmount() {
    this.props.onRef(undefined); // eslint-disable-line react/prop-types
  }

  componentWillReceiveProps(newProps) {
    if (newProps.error) {
      this.setState({ errors: { start: true, end: true } });
    }
  }

  getRelativeDateValues(value, start, end, dateFormat) {
    let parsedStart;
    let parsedEnd;
    if (this.props.isArchival) {
      parsedStart = isValidDate(start, dateFormat)
        ? moment(start, dateFormat)
          .format(dateFormat)
          .toString()
        : moment().subtract(1, 'year')
          .format(dateFormat)
          .toString();
      parsedEnd = isValidDate(start, dateFormat)
        ? moment(end, dateFormat)
          .format(dateFormat)
          .toString()
        : moment().subtract(1, 'year')
          .format(dateFormat)
          .toString();
    }
    else {
      switch (value) {
        case 'yesterday': {
          parsedStart = moment()
            .add(-1, 'days')
            .format(dateFormat)
            .toString();
          parsedEnd = moment()
            .add(-1, 'days')
            .format(dateFormat)
            .toString();
          break;
        }
        case 'last_seven_days': {
          parsedStart = moment()
            .add(-7, 'days')
            .format(dateFormat)
            .toString();
          parsedEnd = moment()
            .add(-1, 'days')
            .format(dateFormat)
            .toString();
          break;
        }
        case 'last_month': {
          parsedStart = moment()
            .add(-1, 'months')
            .startOf('months')
            .format(dateFormat)
            .toString();
          parsedEnd = moment()
            .add(-1, 'months')
            .endOf('months')
            .format(dateFormat)
            .toString();
          break;
        }
        case 'this_month': {
          parsedStart = moment()
            .startOf('months')
            .format(dateFormat)
            .toString();
          parsedEnd = moment()
            .add(-1, 'days')
            .format(dateFormat)
            .toString();
          break;
        }
        case 'this_year': {
          parsedStart = moment()
            .startOf('year')
            .format(dateFormat)
            .toString();
          parsedEnd = moment()
            .add(-1, 'days')
            .format(dateFormat)
            .toString();
          break;
        }
        case 'custom':
        default: {
          parsedStart = (isValidDate(start, dateFormat))
            ? moment(start, dateFormat)
              .format(dateFormat)
              .toString()
            : moment()
              .format(dateFormat)
              .toString();
          parsedEnd = (isValidDate(end, dateFormat))
            ? moment(end, dateFormat)
              .format(dateFormat)
              .toString()
            : moment()
              .format(dateFormat)
              .toString();
          break;
        }
      }
    }

    return {
      parsedStart,
      parsedEnd,
    };
  }

  handleChangeDates(value) {
    const { dateFormat } = this.props;
    this.setState({
      dates: {
        startDate: moment(value.startDate, dateFormat).format(dateFormat).toString(),
        endDate: moment(value.endDate, dateFormat).format(dateFormat).toString(),
      },
      preset: 'custom',
    });
  }

  handleChangeDateSelect(key) {
    const { dateFormat } = this.props;
    const { dates } = this.state;

    return (value) => {
      const updatedDates = this.mergeNewDate(dates, key, value, dateFormat);

      this.setState({
        tempDates: updatedDates,
        dates: updatedDates,
        preset: 'custom',
      });
    };
  }

  selectRelativeDate(value, start, end) {
    const { dateFormat } = this.props;
    const normalizedStart = start || this.state.dates.startDate;
    const normalizedEnd = end || this.state.dates.endDate;

    const { parsedStart, parsedEnd } = this.getRelativeDateValues(value, normalizedStart, normalizedEnd, dateFormat);

    this.setState({
      dates: {
        startDate: parsedStart,
        endDate: parsedEnd,
      },
      preset: value,
    });
    this.dateIsValid();
  }

  handleChangeDateText(key) {
    const { dateFormat } = this.props;
    const { dates } = this.state;

    return (event) => {
      const value = cloneDeep(event.target.value);
      const updatedDates = this.mergeNewDate(dates, key, value, dateFormat);

      this.setState({
        tempDates: updatedDates,
        preset: 'custom',
      });
    };
  }
  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.dates !== nextState.dates || this.state.tempDates !== nextState.tempDates) {
      return true;
    } else {
      return false;
    }
  }
  handleOnSubmit() {
    if (this.state.preset === 'custom') {
      this.props.onApply(this.props.id, {
        startDate: this.state.dates.startDate,
        endDate: this.state.dates.endDate,
      });
    } else {
      this.props.onApply(this.props.id, {
        startDate: this.state.preset,
      });
    }
  }

  validateDateField(event) {
    const error = this.props.translations.validDateError;
    const keyCode = event.charCode || event.which;
    const numbersOnly = (keyCode >= 48 && keyCode <= 57) || keyCode === 47;
    return !numbersOnly ? this.displayError(event, error) : this.dateIsValid();
  }

  checkDate(event) {
    const error = this.props.translations.validDateError;
    const d = event.target.value;
    const isValid = (isValidDate(d, this.props.dateFormat));
    return !isValid ? this.displayError(event, error) : this.combineDates();
  }

  combineDates() {
    this.setState({
      dates: this.state.tempDates,
    });
    this.dateIsValid();
  }

  mergeNewDate(dates, key, value, dateFormat) {
    const newDateValue = (!isValidDate(value, dateFormat))
      ? value
      : moment(value, dateFormat).format(dateFormat).toString();
    const newDatePart = { [key]: newDateValue };
    const updatedDates = {
      ...dates,
      ...newDatePart,
    };

    if (moment(updatedDates.startDate, dateFormat).isAfter(moment(updatedDates.endDate, dateFormat))) {
      if (key === 'startDate') {
        updatedDates.endDate = newDateValue;
      } else {
        updatedDates.startDate = newDateValue;
      }
    }

    return updatedDates;
  }

  displayError(event, error) {
    const errors = { ...this.state.errors };
    errors[event.target.id] = error;
    this.setState({
      errors,
    });
    return false;
  }

  dateIsValid() {
    const errors = {
      start: null,
      end: null,
    };

    this.setState({ errors });
  }

  attachBindings() {
    this.handleChangeDates = this.handleChangeDates.bind(this);
    this.selectRelativeDate = this.selectRelativeDate.bind(this);
    this.handleChangeDateSelect = this.handleChangeDateSelect.bind(this);
    this.handleChangeDateText = this.handleChangeDateText.bind(this);
    this.handleOnSubmit = this.handleOnSubmit.bind(this);
    this.validateDateField = this.validateDateField.bind(this);
    this.checkDate = this.checkDate.bind(this);
    this.mergeNewDate = this.mergeNewDate.bind(this);
    this.displayError = this.displayError.bind(this);
  }

  get fromLabelContent() {
    const { errors } = this.state;
    return (
      <span>
        {this.props.translations.from}
        {errors.start && <div className="message-error">{errors.start}</div>}
      </span>
    );
  }

  get toLabelContent() {
    const { errors } = this.state;
    return (
      <span>
        {this.props.translations.to}
        {errors.end && <div className="message-error">{errors.end}</div>}
      </span>
    );
  }

  get dateRanges() {
    return this.availableRanges.map(({ value, text }) => {
      const rangeClasses = classNames('rdr-PredefinedRangesItem', {
        'rdr-PredefinedRangesItemActive': value === this.state.preset,
      });

      return (
        <button
          className={rangeClasses}
          key={value}
          onClick={() => { this.selectRelativeDate(value); }}
          disabled={this.props.isArchival} 
        >
          {text}
        </button>
      );
    });
  }

  setCurrentLanguageForCalender(language) {
    if (language == 'fr-CA') {
      return 'fr';
    } else if (language == 'zh-Hans' || language == 'zh-Hant') {
      return 'cn';
    }
  }

  render() {
    const { dateFormat, translations, language } = this.props;
    const { errors, dates, tempDates } = this.state;

    // work around limitation of calendar
    const isSameMonth = moment(dates.startDate, dateFormat).isSame(moment(dates.endDate, dateFormat), 'month');
    const startRangeEnd = (isSameMonth)
      ? dates.endDate
      : moment(dates.startDate, dateFormat).endOf('months').format(dateFormat).toString();
    const startClasses = (isSameMonth)
      ? {
        calendar: 'rdr-Calendar',
      }
      : {
        calendar: 'rdr-Calendar dangling-start',
      };

    const daysOfWeek = [translations.sun, translations.mon, translations.tue, translations.wed,
      translations.thu, translations.fri, translations.sat];
	 const monthsOfYear = [translations.jan,translations.feb,translations.mar,translations.apr,translations.may,translations.jun,
		translations.jul,translations.aug,translations.sep,translations.oct,translations.nov,translations.dec];
   
    return (
      <div className="dual-calendar">
        <div className="dual-calendar__top">
          <div className="rdr-PredefinedRanges">
            {this.dateRanges}
          </div>
          <div className="dual-calendar__pairs">
            <div className="rdr-DateRange">
              <Calendar
                classNames={startClasses}
                format={dateFormat}
                link={false}
                onChange={this.handleChangeDateSelect('startDate')}
                onlyClasses
                range={{ startDate: moment(dates.startDate, dateFormat), endDate: moment(startRangeEnd, dateFormat) }}
                shownDate={moment(dates.startDate, dateFormat)}
                lang={this.setCurrentLanguageForCalender(language)}
                theme={moment.updateLocale('en', {
                  weekdaysMin: daysOfWeek,
                  months: monthsOfYear,
                })}

                maxDate={this.props.isArchival ? moment().subtract(1, 'year').format(dateFormat).toString() : this.props.isFutureDateDisable ? moment().format(dateFormat).toString() : this.props.maxDate}
                />
              <Calendar
                maxDate={this.props.isArchival ? moment().subtract(1, 'year').format(dateFormat).toString() : this.props.isFutureDateDisable ? moment().format(dateFormat).toString() : this.props.maxDate}
                format={dateFormat}
                link={false}
                onChange={this.handleChangeDateSelect('endDate')}
                onlyClasses
                range={{ startDate: moment(dates.startDate, dateFormat), endDate: moment(dates.endDate, dateFormat) }}
                shownDate={moment(dates.endDate, dateFormat)}
                lang={this.setCurrentLanguageForCalender(language)}
                theme={moment.updateLocale('en', {
                  weekdaysMin: daysOfWeek,
                  months: monthsOfYear,
                })}
              />
            </div>
            <div className="dual-calendar__inputs">
              <TextField
                fieldClass="field"
                htmlFor="start"
                labelContent={this.fromLabelContent}
                labelClass="field-label"
                error={errors.start}
              >
                <input
                  className="field-input"
                  type="text"
                  id="start"
                  pattern="\d{1,2}/\d{1,2}/\d{4}"
                  placeholder={dateFormat}
                  onKeyPress={this.validateDateField}
                  value={tempDates.startDate}
                  onChange={this.handleChangeDateText('startDate')}
                  onBlur={this.checkDate}
                />
              </TextField>
              <TextField
                fieldClass="field"
                htmlFor="end"
                labelContent={this.toLabelContent}
                labelClass="field-label"
                error={errors.end}
              >
                <input
                  className="field-input"
                  type="text"
                  id="end"
                  pattern="\d{1,2}/\d{1,2}/\d{4}"
                  placeholder={dateFormat}
                  onKeyPress={this.validateDateField}
                  value={tempDates.endDate}
                  onChange={this.handleChangeDateText('endDate')}
                  onBlur={this.checkDate}
                />
              </TextField>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

DualCalendar.propTypes = {
  startDate: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object,
  ]),
  endDate: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object,
  ]),
  dateFormat: PropTypes.string,
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  onApply: PropTypes.func.isRequired,
  translations: PropTypes.object,
  language: PropTypes.string,
  isArchival: PropTypes.bool,
  isFutureDateDisable: PropTypes.bool,
};

DualCalendar.defaultProps = {
  startDate: '',
  endDate: '',
  dateFormat: 'MM/DD/YYYY',
  translations: {
    yesterday: 'Yesterday',
    lastSevenDays: 'Last 7 Days',
    lastWeek: 'Last Week',
    lastThirtyDays: 'Last 30 Days',
    lastMonth: 'Last month',
    thisMonth: 'This Month',
    lastYear: 'Last Year',
    thisYear: 'This Year',
    custom: 'Custom',
    from: 'From',
    to: 'To',
    apply: 'Apply',
    cancel: 'Cancel',
    validDateError: 'Please enter a valid date.',
    language: 'en-US',
  },
  right: false,
  isArchival: false,
  isFutureDateDisable: false,
};

export default DualCalendar;